// SpotlightTour — generic coach-mark overlay on REAL UI elements.
// Spotlight hole via box-shadow trick; targets are CSS selectors so
// the tour always runs on the live screen (no fake data).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const PAD = 8;
const CARD_W = 320;
const CARD_H_EST = 230;

export default function SpotlightTour({ steps, onFinish, idPrefix = "tour", doneLabel }) {
  const { t } = useTranslation("common");
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const finishedRef = useRef(false);
  const step = steps[idx];

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinish();
  }, [onFinish]);

  // Measure the active target on a short interval — survives smooth
  // scrolling, image loads and layout shifts.
  useEffect(() => {
    if (!step) return undefined;
    let scrolled = false;
    const measure = () => {
      const el = step.target ? document.querySelector(step.target) : null;
      if (!el) {
        setRect(null);
        return;
      }
      if (!scrolled) {
        scrolled = true;
        const r0 = el.getBoundingClientRect();
        if (r0.top < 70 || r0.bottom > window.innerHeight - 140) {
          el.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    const iv = setInterval(measure, 150);
    return () => clearInterval(iv);
  }, [idx, step]);

  // ESC = skip (persists the flag too — tour never auto-reappears).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") finish();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [finish]);

  if (!step) return null;
  const isLast = idx === steps.length - 1;
  const next = () => (isLast ? finish() : setIdx((i) => i + 1));

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let tipStyle;
  if (rect) {
    const left = Math.min(Math.max(rect.left, 16), Math.max(16, vw - CARD_W - 16));
    const belowTop = rect.top + rect.height + PAD + 14;
    let top;
    if (belowTop + CARD_H_EST <= vh) top = belowTop;
    else if (rect.top - CARD_H_EST - 14 >= 0) top = rect.top - CARD_H_EST - 14 + PAD;
    else top = Math.max(24, vh / 2 - CARD_H_EST / 2);
    tipStyle = { position: "fixed", left, top, width: CARD_W, zIndex: 98 };
  } else {
    tipStyle = {
      position: "fixed",
      left: "50%",
      top: "50%",
      transform: "translate(-50%,-50%)",
      width: CARD_W,
      zIndex: 98,
    };
  }

  return createPortal(
    <div data-testid={`${idPrefix}-overlay`}>
      {/* Click blocker — the tour owns the screen while active */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 95 }}
        onClick={(e) => e.stopPropagation()}
      />
      {rect ? (
        <div
          data-testid={`${idPrefix}-spotlight`}
          className="ring-2 ring-amber-400/80"
          style={{
            position: "fixed",
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 14,
            boxShadow: "0 0 0 9999px rgba(2,2,8,0.82)",
            transition: "top 250ms ease, left 250ms ease, width 250ms ease, height 250ms ease",
            pointerEvents: "none",
            zIndex: 96,
          }}
        />
      ) : (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2,2,8,0.82)",
            pointerEvents: "none",
            zIndex: 96,
          }}
        />
      )}

      <div
        data-testid={`${idPrefix}-card`}
        style={tipStyle}
        className="rounded-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-300"
      >
        {steps.length > 1 && (
          <div className="mb-2 flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-5 bg-amber-400" : "w-1.5 bg-zinc-700"
                }`}
              />
            ))}
            <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">
              {t("tour.step", { current: idx + 1, total: steps.length })}
            </span>
          </div>
        )}
        <div className="text-base font-medium text-zinc-100">{step.title}</div>
        <p className="mt-1.5 text-sm leading-relaxed text-zinc-400">{step.body}</p>
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            data-testid={`${idPrefix}-skip`}
            onClick={finish}
            className="text-xs text-zinc-500 transition hover:text-zinc-300"
          >
            {t("tour.skip")}
          </button>
          <button
            type="button"
            data-testid={`${idPrefix}-next`}
            onClick={next}
            className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-zinc-950 transition hover:bg-amber-400"
          >
            {isLast ? doneLabel || t("tour.done") : t("tour.next")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
